/**
 * What an operator console shows when an uploaded picture will not load.
 *
 * ## The report this exists for
 *
 * *"Seems images uploaded do not render on admin panel."* The app icon, the splash and the
 * sidebar logo were each fetched, refused by the browser, and drawn as an empty box. An empty
 * box is indistinguishable from an asset nobody has uploaded, so the console's answer to "where
 * is my icon" was the same shape as its answer to "you have no icon", and the operator could
 * not tell which they were being told.
 *
 * The cause was a response header. The API stamped `Cross-Origin-Resource-Policy: same-origin`
 * on every reply, and the console is served from a different host than the API, so the browser
 * fetched each image — 200, right `Content-Type`, all the bytes — read that header, discarded
 * the response and fired `onerror`. Nothing was logged to the JavaScript console. The header is
 * fixed on the server; this is the half that would have named the problem on the first day.
 *
 * The balance headers were the one thing on that page that worked, which is the detail that
 * makes the diagnosis rather than muddies it: their preview carries `crossOrigin="anonymous"`
 * so it could measure the picture's brightness, that makes the request a CORS request, and the
 * cross-origin-resource-policy check is skipped for anything that is not `no-cors`. One image
 * on the screen rendering while the others did not is what a wrong header looks like.
 *
 * ## Why a panel and not a placeholder
 *
 * A placeholder is what a *customer* surface should draw: a bank mark that failed should
 * degrade to a grey square, because a customer can do nothing about it and a red box in a
 * transfer flow reads as their transfer being broken. An operator is the opposite — they are
 * the person who can fix it, this is the screen they came to specifically to look at the
 * artwork, and the thing they most need is the address that failed. A 404 at that address means
 * the asset is not where the API said it was; a 401 means it needs a token an `<img>` cannot
 * send; a page that loads the picture fine on its own means the browser refused those exact
 * bytes to this console, which is the header case above.
 *
 * ## Why it is not a self-contained `<AssetImage>`
 *
 * It was, briefly. Every caller here draws one URL into more than one element (the icon at four
 * sizes, the logo on light and on dark) or draws it inside a frame that would ruin the message
 * (the header preview lays a dark scrim over its contents; the splash frame clips to a phone
 * outline and hides overflow). So the state has to live in the section, not in the element, and
 * what is worth sharing is the panel and the one-line hook that drives it.
 */
import { useEffect, useState } from 'react';

import { IconAlert } from './icons.js';

/**
 * Track whether the picture at `src` refused to load, resetting whenever it changes.
 *
 * The reset is the part that is easy to leave out and painful to leave out: without it, an
 * operator who replaced a broken asset was still shown the old failure over their new upload,
 * so a fix looked exactly like the bug.
 */
export function useAssetFailure(src: string | null): {
  readonly failed: boolean;
  readonly onError: () => void;
} {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  return { failed, onError: () => setFailed(true) };
}

/**
 * The panel drawn in place of the picture.
 *
 * `error-box` rather than the amber `hero-advice`, deliberately and against the argument that
 * style's own comment makes. Amber is for advice about artwork an operator is entitled to use;
 * a red box over a picture they may legitimately keep reads as a bug in the console. This is
 * not that. The console asked the API for a file, was refused, and cannot show them what they
 * uploaded — which is a failure, and saying so quietly is what let the last one run unreported.
 */
export function AssetFailure({ label, src }: { label: string; src: string }): JSX.Element {
  return (
    <div className="error-box" role="alert" style={{ width: '100%' }}>
      <IconAlert size={18} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{label} did not load</div>
        {/*
          Neutral about *why*, because this panel serves both a stored asset fetched from the
          API and a file the operator has only just picked, and a sentence asserting the wrong
          one of those is worse than a sentence asserting neither. The address does the work: it
          is either an API URL, whose status answers the question, or a local `blob:` URL, which
          says the browser could not decode the chosen file.
        */}
        <div style={{ fontSize: 12.5, marginTop: 2 }}>
          The browser fetched this address and would not draw what came back. Open it in a new
          tab: a 404 means the asset is not where the API says it is, and a page that shows the
          picture on its own means the browser refused those bytes to this console.
        </div>
        <div className="mono" style={{ fontSize: 11.5, marginTop: 6, overflowWrap: 'anywhere' }}>
          {src}
        </div>
      </div>
    </div>
  );
}
